import { FC, useEffect, useMemo, useRef, useState } from "react";
// 组件
import { ProAutoComplete } from '../antd';
import OptionLabel from "./subpage/optionLabel";
// 方法
import classNames from "classnames";
// 常量
import { API_STATUS_ENUM } from "../constants/enum";
import { SUCCESS_STATUS_CODE } from "../constants/constant";
import { OPTION_KEY, SuperAutoCompleteProps } from "./interface";
import './style.less';

/** 自动搜索+可输入 */
const SuperAutoComplete: FC<SuperAutoCompleteProps> = props => {
  const {
    className, style, otherParams, cacheKey, loadingType, focusReset, popupClassName, allowClear, defaultValue, disabled, dataKey,
    options: outOptions, labelKey = OPTION_KEY.LABEL, valueKey = OPTION_KEY.VALUE, value: outValue, placeholder,
    fetchApi, formatOptions, onComplete, onFocus, onSearch, onChange, onClear, beforeFetch,
  } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [value, setValue] = useState<string>();
  const [searchValue, setSearchValue] = useState<string>();
  const [options, setOptions] = useState<any[]>();
  const apiStatus = useRef<API_STATUS_ENUM>(API_STATUS_ENUM.NOT_START); // 接口状态
  const timer = useRef<any>(); // 延时过滤选项定时器
  useEffect(() => {
    if (loadingType === 'init') {
      handleFetchData();
    }
  }, [loadingType]);
  useEffect(() => {
    setValue(outValue);
  }, [outValue]);
  /** 加载数据 */
  const handleFetchData = async() => {
    if (outOptions) {
      setOptions(outOptions);
      return;
    }
    if (!fetchApi) {
      console.error('options和fetchApi至少需要传一个');
      setOptions(undefined);
      return;
    }
    // cacheKey有值，则优先从缓存获取
    if (cacheKey && !focusReset) {
      const CACHE_OPTIONS = window.sessionStorage.getItem(cacheKey);
      // 有缓存且不是错误标识
      if (CACHE_OPTIONS && CACHE_OPTIONS !== API_STATUS_ENUM.ERROR) {
        if (CACHE_OPTIONS === API_STATUS_ENUM.LOADING) {
          // 缓存标识为加载中
          timer.current && clearInterval(timer.current);
          timer.current = setInterval(() => {
            const resData = window.sessionStorage.getItem(cacheKey);
            if (resData && resData !== API_STATUS_ENUM.LOADING) {
              clearInterval(timer.current);
              const data = JSON.parse(resData);
              handleDataProcessing(data);
            }
          }, 100);
        } else {
          // 成功从缓存中取到数据
          const data = JSON.parse(CACHE_OPTIONS);
          handleDataProcessing(data);
        }
        return;
      }
    }
    setLoading(true);
    apiStatus.current = API_STATUS_ENUM.LOADING;
    if (cacheKey) {
      window.sessionStorage.setItem(cacheKey, API_STATUS_ENUM.LOADING);
    }
    let newParams: any = {...otherParams};
    if (beforeFetch) {
      newParams = beforeFetch(otherParams);
      if (newParams === false) return;
    }
    const res = await fetchApi(newParams);
    setLoading(false);
    if (res.code === SUCCESS_STATUS_CODE) {
      apiStatus.current = API_STATUS_ENUM.SUCCESS;
      const data = dataKey ? res.data?.[dataKey] : res.data;
      onComplete?.(data);
      if (cacheKey) {
        window.sessionStorage.setItem(cacheKey, JSON.stringify(data));
      }
      handleDataProcessing(data);
      return;
    }
    apiStatus.current = API_STATUS_ENUM.ERROR;
    if (cacheKey) {
      window.sessionStorage.setItem(cacheKey, API_STATUS_ENUM.ERROR);
    }
  };
  /** 获取的数据处理 */
  const handleDataProcessing = (data: any = []) => {
    const newOptions = formatOptions ? formatOptions(data) : data;
    setOptions(newOptions);
  }
  /** 获取焦点回调 */
  const handleFocus = () => {
    if (loadingType === 'focus') {
      if (focusReset) {
        handleFetchData();
      } else {
        if (apiStatus.current === API_STATUS_ENUM.NOT_START || apiStatus.current === API_STATUS_ENUM.ERROR) {
          handleFetchData();
        }
      }
    }
    onFocus?.();
  };
  /** 搜索回调 */
  const handleSearch = (value: string) => {
    setSearchValue(value);
    onSearch?.(value);
  };
  /** 选中 option，或 input 的 value 变化时回调 */
  const handleChange = (value?: string) => {
    const findOption = options?.find(item => item[valueKey] === value);
    setValue(value);
    onChange?.(value, findOption);
  };
  /** 点击清楚按钮回调 */
  const handleClear = () => {
    onClear?.();
  };
  /** 选项 */
  const autoCompleteOptions = useMemo(() => {
    const newOptions: any[] = [];
    if (searchValue) {
      newOptions.push({
        value: searchValue,
      });
    }
    options?.forEach(item => {
      const { [valueKey]: value, [labelKey]: label, } = item;
      if (searchValue) {
        const include = label?.toLowerCase?.()?.includes(searchValue) || value?.toLowerCase?.()?.includes(searchValue);
        if (include) {
          newOptions.push({
            label: (
              <>
                <OptionLabel label={value} searchValue={searchValue} />
                <OptionLabel label={label} searchValue={searchValue} className="option-sub-label" />
              </>
            ),
            value: value,
          });
        }
      } else {
        newOptions.push({
          label: (
            <>
              {!!value && <span>{value}</span>}
              {!!label && <span className="option-sub-label">{label}</span>}
            </>
          ),
          value: value,
        });
      }
    });
    return newOptions;
  }, [options, searchValue]);

  return (
    <ProAutoComplete
      style={style}
      allowClear={allowClear}
      defaultValue={defaultValue}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      className={classNames('yb-auto-complete', className)}
      notFoundContent={loading ? '数据加载中' : '请输入'}
      options={autoCompleteOptions}
      popupClassName={classNames('yb-auto-complete-popup', popupClassName)}
      onFocus={handleFocus}
      onSearch={handleSearch}
      onChange={handleChange}
      onClear={handleClear}
    />
  );
};

SuperAutoComplete.defaultProps = {
  focusReset: false,
  allowClear: true,
  loadingType: 'focus',
  valueKey: OPTION_KEY.VALUE,
  labelKey: OPTION_KEY.LABEL,
};

export default SuperAutoComplete;
